import {keyBy} from 'lodash';
import React, {useCallback} from 'react';
import {themmeColor} from 'src/constants/constants';
import DSAccordion from 'src/DesignSystem/Accordion/DSAccordion';
import Box from 'src/DesignSystem/Box/Box';
import DSButton from 'src/DesignSystem/Button/Button';
import {ArrayChildProps} from 'src/DesignSystem/Form/common/ArrayField/ArrayField';
import {useArrayField} from 'src/DesignSystem/Form/common/ArrayField/useArrayField';
import Text from 'src/DesignSystem/Form/common/Text/Text';
import {
  FONT_WEIGHTS,
  TEXT_TYPES,
} from 'src/DesignSystem/Form/common/Text/Text.types';
import {Form} from 'src/DesignSystem/Form/Form';
import {
  FORM_INPUT_TYPE,
  FormInputProps,
} from 'src/DesignSystem/Form/Form.types';
import {createEmptyFormDataFromFormConfig} from 'src/DesignSystem/Form/Form.utils';
import {
  OverWriteFormFieldsFunctionType,
  useFormData,
} from 'src/DesignSystem/Form/useFormData';
import DeleteIcon from 'src/icons/DeleteIcon';
import DocumentIcon from 'src/icons/DocumentIcon';
import ManualIcon from 'src/icons/ManualIcon';
import {
  InformationSource,
  TaxFormFieldIds,
} from 'src/store/taxForms/taxForms.types';
import {overWriteProfitLossHeader} from 'src/store/taxForms/taxForms.utils';

const HeaderItem = ({
  array,
  arrayObjectIndex,
  onChangeValue,
  childProps,
  childTitle,
}: ArrayChildProps) => {
  const {arrayObject, updateObject} = useArrayField(
    array,
    onChangeValue,
    arrayObjectIndex,
  );

  const onDelete = () => {
    const newValue = array.filter((_, index) => index !== arrayObjectIndex);
    onChangeValue(newValue);
  };

  const overWriteFields: OverWriteFormFieldsFunctionType = useCallback(
    (props) => {
      return overWriteProfitLossHeader({
        ...props,
      });
    },
    [],
  );

  const {fields} = useFormData({
    data: arrayObject,
    setData: updateObject,
    config: childProps,
    overWriteFields,
  });

  const isApplicableSwitchField = fields.find(
    (field) => field.path === TaxFormFieldIds.IS_APPLICABLE,
  );

  return (
    <Box
      borderColor={themmeColor.silver}
      col
      style={{
        padding: 16,
        marginBottom: 12,
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: themmeColor.grey,
              border: `1px solid ${themmeColor.offWhite}`,
              padding: '2px 8px',
            }}>
            <Text
              color={themmeColor.white}
              type={TEXT_TYPES.S}
              fontWeight={FONT_WEIGHTS.SemiBold}
              text={arrayObjectIndex + 1}
            />
          </div>

          {arrayObject[TaxFormFieldIds.SOURCE] ===
          InformationSource.document ? (
            <DocumentIcon />
          ) : (
            <ManualIcon />
          )}
        </div>
        <div style={{display: 'flex', gap: 8}}>
          {arrayObject[TaxFormFieldIds.SOURCE] !==
          InformationSource.document ? (
            <DSButton
              text="Delete"
              startIcon={<DeleteIcon />}
              type="secondary"
              onClick={onDelete}
            />
          ) : null}
          {isApplicableSwitchField ? (
            <Form {...isApplicableSwitchField} />
          ) : null}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0px 12px',
          marginTop: 12,
        }}>
        {fields.map((field) => {
          if (
            [TaxFormFieldIds.SOURCE, TaxFormFieldIds.IS_APPLICABLE].includes(
              field.path as TaxFormFieldIds,
            )
          ) {
            return null;
          }
          return (
            <div style={{marginBottom: 12}}>
              <Form {...field} />
            </div>
          );
        })}
      </div>
    </Box>
  );
};

const ProfitLossHeader = (props: FormInputProps) => {
  if (props.inputType !== FORM_INPUT_TYPE.Array) {
    return null;
  }

  const onAddHeaderItem = () => {
    const oldValue = Array.isArray(props.value) ? props.value : [];
    const newItem = createEmptyFormDataFromFormConfig(props.childProps, 0);
    newItem[TaxFormFieldIds.SOURCE] = InformationSource.manual;
    newItem[TaxFormFieldIds.IS_APPLICABLE] = true;
    const newValue = [newItem, ...oldValue];
    props.onChangeValue(newValue);
  };

  return (
    <div style={{marginTop: 12, marginBottom: 12}}>
      <DSAccordion defaultExpanded name={props.title}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}>
          <DSButton
            type="primary"
            onClick={onAddHeaderItem}
            text={`Add ${props.title}`}
            style={{marginLeft: 'auto'}}
          />
        </div>
        {Array.isArray(props.value)
          ? props.value.map((_, i) => (
              <div>
                <HeaderItem
                  array={props.value}
                  arrayObjectIndex={i}
                  childProps={props.childProps}
                  onChangeValue={props.onChangeValue}
                  disableValidation={props.disableValidation}
                  childTitle={props.childTitle}
                />
              </div>
            ))
          : null}
      </DSAccordion>
    </div>
  );
};

export default ProfitLossHeader;
